﻿import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringMirrorObject } from "../../objects/StringMirrorObject";
import { StringObject } from "../../objects/StringObject";
import { EasingFunctionOutput } from "../../tools/EasingFunctionTool";
import { styleTxn } from "../../utils/style-txn";

type ProgressComputeKernel = {
  computeRawProgress(scroll: number, start: number, difference: number): number;
  computeRawProgressBatch(
    scroll: number,
    starts: Float64Array,
    differences: Float64Array,
    out: Float64Array,
    length: number
  ): void;
};

const DEFAULT_PROGRESS_KERNEL: ProgressComputeKernel = {
  computeRawProgress(scroll: number, start: number, difference: number): number {
    if (difference === 0) return 0;
    const raw = (scroll - start) / difference;
    if (raw <= 0) return 0;
    if (raw >= 1) return 1;
    return raw;
  },
  computeRawProgressBatch(
    scroll: number,
    starts: Float64Array,
    differences: Float64Array,
    out: Float64Array,
    length: number
  ): void {
    for (let i = 0; i < length; i++) {
      const diff = differences[i];
      if (diff === 0) {
        out[i] = 0;
        continue;
      }
      const raw = (scroll - starts[i]) / diff;
      if (raw <= 0) out[i] = 0;
      else if (raw >= 1) out[i] = 1;
      else out[i] = raw;
    }
  },
};

export class StringProgress extends StringModule {
  protected updateScheduled = false;
  private batchStarts: Float64Array = new Float64Array(0);
  private batchDiffs: Float64Array = new Float64Array(0);
  private batchOut: Float64Array = new Float64Array(0);

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "progress";

    this.cssProperties = [
      { name: "--progress", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--progress-slice", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "easing", type: "easing", fallback: this.settings["easing"] },
    ];
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    super.initializeObject(globalId, object, element, attributes);
  }

  private sanitizeRawProgress(rawProgress: number): number {
    if (!Number.isFinite(rawProgress)) return 0;
    if (rawProgress <= 0) return 0;
    if (rawProgress >= 1) return 1;
    const EPS = 1e-4;
    if (rawProgress > 1 - EPS) rawProgress = 1;
    else if (rawProgress < EPS) rawProgress = 0;
    return rawProgress;
  }

  private applyRawProgress(object: StringObject, rawProgress: number): void {
    const normalizedRaw = this.sanitizeRawProgress(rawProgress);

    const prevRawProgress = object.progressRaw;

    if (prevRawProgress === normalizedRaw) {
      return;
    }

    object.progressRaw = normalizedRaw;

    const easing = object.getProperty<EasingFunctionOutput>("easing");
    const progress = typeof easing === "function" ? easing(normalizedRaw) : normalizedRaw;

    object.progress = progress;
  }

  private recomputeProgress(object: StringObject): void {
    const startPosition = object.getProperty<number>("start-position") ?? object.startPosition;
    const differencePosition =
      object.getProperty<number>("difference-position") ?? object.differencePosition;

    object.startPosition = startPosition;
    object.differencePosition = differencePosition;

    const rawProgress = DEFAULT_PROGRESS_KERNEL.computeRawProgress(
      this.data.scroll.transformedCurrent,
      startPosition,
      differencePosition
    );

    this.applyRawProgress(object, rawProgress);
  }

  private ensureBatchCapacity(length: number): void {
    if (this.batchStarts.length >= length) return;
    this.batchStarts = new Float64Array(length);
    this.batchDiffs = new Float64Array(length);
    this.batchOut = new Float64Array(length);
  }

  calculatePositions(object: StringObject, windowSize: number) {
    super.calculatePositions(object, windowSize);
    object.startPosition = object.getProperty<number>("start-position") ?? object.startPosition;
    object.differencePosition =
      object.getProperty<number>("difference-position") ?? object.differencePosition;
    this.recomputeProgress(object);
  }

  onScroll(data: StringData): void {
    super.onScroll(data);
  }

  onObjectConnected(object: StringObject) {
    super.onObjectConnected(object);
  }

  onScrollMeasure(data: StringData) {
    const len = this.objects.length;
    if (len === 0) return;

    this.ensureBatchCapacity(len);
    for (let i = 0; i < len; i++) {
      const object = this.objects[i];
      const startPosition = object.getProperty<number>("start-position") ?? object.startPosition;
      const differencePosition =
        object.getProperty<number>("difference-position") ?? object.differencePosition;
      object.startPosition = startPosition;
      object.differencePosition = differencePosition;
      this.batchStarts[i] = startPosition;
      this.batchDiffs[i] = differencePosition;
    }

    DEFAULT_PROGRESS_KERNEL.computeRawProgressBatch(
      this.data.scroll.transformedCurrent,
      this.batchStarts,
      this.batchDiffs,
      this.batchOut,
      len
    );

    for (let i = 0; i < len; i++) {
      this.applyRawProgress(this.objects[i], this.batchOut[i]);
    }
  }

  onMutate() {
    styleTxn.run(() => {
      const len = this.objects.length;
      for (let i = 0; i < len; i++) {
        this.updateObjectProgress(this.objects[i]);
      }
    });
  }

  private updateObjectProgress(object: StringObject): void {
    const progress = object.progress;
    const prevProgress = object.getProperty<number>("progress-applied");

    if (prevProgress === progress) {
      return;
    }
    const key = object.getProperty<string>("key");
    object.setProperty<number>("progress-applied", progress);
    const rawProgress = object.progressRaw ?? progress;
    const parentEasing = object.getProperty<EasingFunctionOutput>("easing");

    if (object.eventProgressName) {
      this.events.emit(object.eventProgressName, progress);
    }

    if (key) {
      this.applyVarToElement(object, key, progress);
    }

    // Handle mirrors
    for (let i = 0; i < object.mirrorObjects.length; i++) {
      const mirror = object.mirrorObjects[i];
      const eased = mirror.applyProgress(
        rawProgress,
        typeof parentEasing === "function" ? parentEasing : undefined,
      );
      mirror.setProperty("progress", eased);
      if (key) {
        this.tools.styleTxn.setVar(mirror.htmlElement, key, eased);
      }
    }
  }

  onObjectDisconnected(object: StringObject): void {
    super.onObjectDisconnected(object);
    const key = object.getProperty<string>("key");
    if (!key) return;

    const clearCustomProperty = (el: HTMLElement) => {
      el.style.removeProperty(key);
    };

    clearCustomProperty(object.htmlElement);
    const mirrors = object.mirrorObjects;
    for (let i = 0; i < mirrors.length; i++) {
      clearCustomProperty(mirrors[i].htmlElement);
    }
  }
}
